import { useState } from 'react'
import { ModalShell, ModalHead, ModalSection, ModalLabel, ModalFoot } from '@/ui/Modal'
import { Input } from '@/ui'
import { useLeadWrites } from '@/features/leads/workflow/useLeadMutations'
import { trackEvent } from '@/shared/api/telemetry'
import {
  composeReferralNote, emptyReferral, isReferralComplete, referralContactFields,
  referralPrimaryKey, type ReferralDraft,
} from './referral'
import type { Lead } from '@/shared/types'

interface Props {
  lead: Lead
  onCancel: () => void
  /** Hands the composed note to the outcome flow, which logs the call itself. */
  onDone: (note: string) => void
}

const fieldCls = 'w-full'

export default function ReferralSheet({ lead, onCancel, onDone }: Props) {
  const { addContact, setPrimaryContact } = useLeadWrites()
  const [draft, setDraft] = useState<ReferralDraft>(() => emptyReferral(lead.contact))
  const [makePrimary, setMakePrimary] = useState(true)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState('')
  const set = (k: keyof ReferralDraft) => (v: string) => setDraft(d => ({ ...d, [k]: v }))

  const save = async () => {
    if (saving || !isReferralComplete(draft)) return
    setSaving(true)
    setFailed('')
    try {
      await addContact(lead.id, referralContactFields(draft))
    } catch (err) {
      setSaving(false)
      setFailed(err instanceof Error ? err.message : "Kunde inte lägga till den hänvisade kontakten. Försök igen.")
      return
    }
    if (makePrimary) {
      setPrimaryContact(lead.id, referralPrimaryKey(draft), draft.name.trim())
    }
    // Names, phone and email stay out of it — what the analytics needs is that a
    // referral happened, whether the gatekeeper was named, and how reachable the
    // referred person is.
    trackEvent('referral_captured', {
      primary: makePrimary,
      spokeWith: !!draft.spokeWith.trim(),
      phone: !!draft.phone.trim(),
      email: !!draft.email.trim(),
    }, lead.id)
    onDone(composeReferralNote(draft))
  }

  return (
    <ModalShell onClose={onCancel}>
      <ModalHead
        eyebrow="Steg 2 · registrera hänvisning"
        title={<>Hänvisad vidare · {lead.company}</>}
        onClose={onCancel}
      />

      <ModalSection>
        <ModalLabel>Vem pratade du faktiskt med?</ModalLabel>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            type="text"
            className={fieldCls}
            placeholder="Namn på personen som svarade"
            value={draft.spokeWith}
            onChange={e => set('spokeWith')(e.target.value)}
            autoFocus
          />
          <Input
            type="text"
            className={fieldCls}
            placeholder="Deras roll (reception, assistent…)"
            value={draft.spokeWithRole}
            onChange={e => set('spokeWithRole')(e.target.value)}
          />
        </div>
        <p className="text-[11px] text-ink-500 mt-1.5 mb-0">
          Förifylld med den listade kontakten. Ändra om någon annan svarade.
        </p>
      </ModalSection>

      <ModalSection>
        <ModalLabel>Vem hänvisade de dig till?</ModalLabel>
        <div className="grid gap-2 sm:grid-cols-2">
          <Input
            type="text"
            className={fieldCls}
            placeholder="Namn (obligatoriskt)"
            value={draft.name}
            onChange={e => set('name')(e.target.value)}
          />
          <Input
            type="text"
            className={fieldCls}
            placeholder="Roll (t.ex. kontorsansvarig)"
            value={draft.role}
            onChange={e => set('role')(e.target.value)}
          />
          <Input
            type="tel"
            className={fieldCls}
            placeholder="Telefon"
            value={draft.phone}
            onChange={e => set('phone')(e.target.value)}
          />
          <Input
            type="email"
            className={fieldCls}
            placeholder="E-post"
            value={draft.email}
            onChange={e => set('email')(e.target.value)}
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-[12px] text-ink-700 cursor-pointer select-none mt-2.5">
          <input
            type="checkbox"
            checked={makePrimary}
            onChange={e => setMakePrimary(e.target.checked)}
            className="accent-brand"
          />
          Ring dem nästa gång — gör dem till primär kontakt
        </label>
        {failed && <p className="text-[11px] text-terracotta mt-2 mb-0">{failed}</p>}
      </ModalSection>

      <ModalFoot
        onCancel={onCancel}
        onSave={() => { void save() }}
        saveDisabled={saving || !isReferralComplete(draft)}
        saveLabel={saving ? 'Sparar…' : 'Lägg till kontakt & logga samtalet'}
      />
    </ModalShell>
  )
}
